import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import CreateUpdateGuardCommon, {FINGER_NAME} from "./CreateUpdateGuardCommon";
import {AddGuardDocument, CreateGuardUser} from "../../../../reduxToolkit/Guards/GuardsApi";
import {accessMethodModel} from "../../../../enums/accessMethodEnum";
import {errorMsg} from "../../../../Apis/functions";
import {getPhotoPicker} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import PasswordGenerator from "../../../../Helpers/PasswordGenerator";
import routes from "../../../../Routes/routes";
import {GUARD_CREATE_STEPS, GUARD_OPTIONAL_STEPS, GUARD_ROSTER_PERMISSION} from "../Model/Model";

/**
 * Creation of a guard in steps: details, contract, documents, photo, fingerprint and summary. Once
 * created, its credentials are shown and the list of the guards is opened.
 */
const CreateGuardApp = () => {
    const {
        t, navigate, DEV, permission, dispatch, disabled, activeStep, setActiveStep,
        handleStepChange, loadingProgress, setLoadingProgress, capturing, setCapturing,
        handleCloseModal, userData, setUserData, handleFormChangeUserData, handleClearUserData,
        emptyFields, setEmptyFields, isValidEmail, isValidDocumentId, isValidPhoneNumber,
        isValidDob, setIsValidDob, isValidPin, handleValidationStepDetails, employeeData,
        setEmployeeData, handleFormChangeEmployeeData, isValidEndDate, setIsValidEndDate,
        zoneAccesses, zoneAccessIds, setZoneAccessIds, documents, addDocument, removeDocument,
        photoPickerFile, setPhotoPickerFile, isSelfieValid, emptySelfie, setEmptySelfie,
        isPilgrimsValidation, selectedReader, setSelectedReader, availableReaders,
        setAvailableReaders, getAvailableReaders, capturedFingerprint, setCapturedFingerprint,
        fingerprintSrc, setFingerprintSrc, fingerprintsList, fingerprintsTemp, setFingerprintsTemp,
        mergedFingerprints, handleUploadFinger, fingerprintName,
        setFingerprintName, updated, setUpdated, updatedFinger, setUpdatedFinger, setModified,
        handleValidationFinger, assignZoneAccess, uploadUserImage, secretModal, setSecretModal,
        secretKey, setSecretKey,
    } = CreateUpdateGuardCommon();
    const {passwordGenerator} = PasswordGenerator();
    const [pendingDocument, setPendingDocument] = useState(false);
    const [emptyDocument, setEmptyDocument] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState(null);
    const [createdUserId, setCreatedUserId] = useState(null);

    const canCreate = DEV || permission?.includes(GUARD_ROSTER_PERMISSION.CREATE);

    /**
     * A contract only requires its start date, the zone accesses are optional.
     */
    const handleValidationContract = () => {
        if (employeeData.isValid() && isValidEndDate) {
            setActiveStep((prev) => prev + 1);
            setEmptyFields(false);
        } else {
            setEmptyFields(true);
        }
    };

    // The photo is optional unless the company validates it
    const isStepOptional = (step) =>
        GUARD_OPTIONAL_STEPS.includes(step) || (step === GUARD_CREATE_STEPS.PHOTO && !isPilgrimsValidation);

    /**
     * The photo is only required when the company validates it, as the one of an employee.
     */
    const handleValidationPhoto = () => {
        if (isPilgrimsValidation && !isSelfieValid) {
            setEmptySelfie(true);
            return;
        }
        setEmptySelfie(false);
        setActiveStep((prev) => prev + 1);
    };

    /**
     * Keeps whether a document is half captured in the documents step, its fields stop being
     * marked once it is added or cleared.
     */
    const handlePendingDocument = (pending) => {
        setPendingDocument(pending);
        if (!pending) setEmptyDocument(false);
    };

    /**
     * The documents are optional, but a document already captured has to be added or cleared.
     */
    const handleValidationDocuments = () => {
        if (pendingDocument) {
            setEmptyDocument(true);
            return;
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleCreateGuard = () => {
        if (isPilgrimsValidation && !getPhotoPicker(photoPickerFile)) {
            setEmptySelfie(true);
            return;
        }
        const password = userData.password?.trim() !== '' ? userData.password : passwordGenerator();
        const body = {
            employee: employeeData.set(),
            user: userData.set(password),
        };

        dispatch(CreateGuardUser(body)).then(({payload: {data: {data, success, code}}}) => {
            if (!success) {
                errorMsg(code);
                return;
            }
            const userId = data?.user?.id;
            fingerprintsTemp.forEach((finger) =>
                uploadUserImage(userId, accessMethodModel.FINGER_PRINT.id, finger.name, finger.file, FINGER_NAME));
            const imageProfile = getPhotoPicker(photoPickerFile);
            if (imageProfile) {
                uploadUserImage(userId, accessMethodModel.FACE_RECOGNITION.id, 'Face recognition', imageProfile);
            }
            documents.forEach((document) => dispatch(AddGuardDocument({userId, name: document.name, file: document.file})));
            if (zoneAccessIds.length > 0) assignZoneAccess(userId);

            toast.success(t('has_been_created_successfully'));
            setSecretKey(data?.user?.secret);
            setGeneratedPassword(userData.password?.trim() !== '' ? null : password);
            setSecretModal(true);
            // Set once the modal is open, the list is opened when the modal closes
            setCreatedUserId(userId);
        });
    };

    /**
     * Once the credentials of the created guard are shown, it goes back to the list of the guards,
     * however the modal is closed.
     */
    useEffect(() => {
        if (createdUserId && !secretModal) navigate(routes.guards.list);
    }, [createdUserId, secretModal]);

    const onSecretModalHide = () => {};

    return {
        t,
        navigate,
        canCreate,
        disabled,
        activeStep,
        setActiveStep,
        handleStepChange,
        isStepOptional,
        loadingProgress,
        setLoadingProgress,
        capturing,
        setCapturing,
        handleCloseModal,
        userData,
        setUserData,
        handleFormChangeUserData,
        handleClearUserData,
        emptyFields,
        setEmptyFields,
        isValidEmail,
        isValidDocumentId,
        isValidPhoneNumber,
        isValidDob,
        setIsValidDob,
        isValidPin,
        handleValidationStepDetails,
        employeeData,
        setEmployeeData,
        handleFormChangeEmployeeData,
        isValidEndDate,
        setIsValidEndDate,
        zoneAccesses,
        zoneAccessIds,
        setZoneAccessIds,
        handleValidationContract,
        documents,
        addDocument,
        removeDocument,
        emptyDocument,
        handlePendingDocument,
        handleValidationDocuments,
        photoPickerFile,
        setPhotoPickerFile,
        emptySelfie,
        handleValidationPhoto,
        isPilgrimsValidation,
        selectedReader,
        setSelectedReader,
        availableReaders,
        setAvailableReaders,
        getAvailableReaders,
        capturedFingerprint,
        setCapturedFingerprint,
        fingerprintSrc,
        setFingerprintSrc,
        fingerprintsList,
        fingerprintsTemp,
        setFingerprintsTemp,
        mergedFingerprints,
        handleUploadFinger,
        fingerprintName,
        setFingerprintName,
        updated,
        setUpdated,
        updatedFinger,
        setUpdatedFinger,
        setModified,
        handleValidationFinger,
        handleCreateGuard,
        secretModal,
        setSecretModal,
        secretKey,
        setSecretKey,
        generatedPassword,
        onSecretModalHide,
    };
};

export default CreateGuardApp;
